import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import {
  AppBar,
  Box,
  Button,
  Collapse,
  Container,
  Grid,
  FormControl,
  MenuItem,
  InputLabel,
  Select,
  Toolbar,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import BadgeIcon from "@mui/icons-material/Badge";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewAgendaIcon from "@mui/icons-material/ViewAgenda";
import { Clear as ClearIcon } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import QrCodeIcon from "@mui/icons-material/QrCode";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import BarChartIcon from "@mui/icons-material/BarChart";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useDispatch, useSelector } from "react-redux";
import {
  beginListeningToTickets,
  checkInTicket,
  createOrder,
  fetchSessionsForEvent,
  updateBadgeForTicket,
} from "../../../redux/features/adminEventsSlice";
import { fetchAttendeeEventCounts } from "../../../redux/features/attendeesSlice";
import {
  filterTicketsWithSearch,
  filterTicketsBySession,
  filterByTicketType,
  filterByGroupSize,
  filterByCheckInStatus,
  calculateGroupSizes,
  calculateOrderGroupCounts,
  getOrderGroupKey,
  ticketStats,
  sortTicketsByOrder,
} from "./utils";
import { MobileFiltersPanel } from "./MobileFiltersPanel";
import { TicketList } from "./TicketList";
import NewTicketModal from "./modals/NewTicketModal";
import SelectSessionToCheckInModal from "./modals/SelectSessionToCheckInModal";
import BadgeDetailsModal from "./modals/BadgeDetailsModal";
import ProgramBadgeModel from "./modals/ProgramBadgeModel";
import ProgramBadgesModal from "./modals/ProgramBadgesModal";
import ScanTicketsModal from "./modals/ScanTicketsModal";
import CheckInsInfoModal from "./modals/CheckInsInfoModal";
import FullScreenDialog from "./modals/FullScreenDialog";
import logger from "../../../utils/logger";

const EMPTY_EVENT_COUNT_BY_ATTENDEE_ID = Object.freeze({});

const EventTickets = () => {
  const { event, handleShowQrCode } = useOutletContext();
  const navigate = useNavigate();
  const { hash, search, pathname } = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  let query = useMemo(() => {
    return new URLSearchParams(search);
  }, [search]);
  const dispatch = useDispatch();
  const {
    id: eventId,
    organizationId,
    tickets,
    sessions,
    userIsAdmin,
    ticketTypes,
    publicUrl,
  } = event || {};

  const attendeeIdsForEventCounts = useMemo(() => {
    if (!tickets?.length) return [];
    const ids = new Set();
    tickets.forEach((t) => {
      const email = (t.displayEmail || "").trim().toLowerCase();
      if (email) ids.add(email);
    });
    return [...ids];
  }, [tickets]);

  const eventCountByAttendeeId = useSelector(
    (state) =>
      state.attendees?.eventCountByAttendeeId?.[organizationId] ??
      EMPTY_EVENT_COUNT_BY_ATTENDEE_ID,
  );

  useEffect(() => {
    if (organizationId && attendeeIdsForEventCounts.length > 0) {
      dispatch(
        fetchAttendeeEventCounts({
          organizationId,
          attendeeIds: attendeeIdsForEventCounts,
        }),
      );
    }
  }, [dispatch, organizationId, attendeeIdsForEventCounts]);
  const [searchText, setSearchText] = useState("");
  const [programBadgeTicketId, setProgramBadgeTicketId] = useState(null);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [selectSessionToCheckInModalOpen, setSelectSessionToCheckInModalOpen] =
    useState(false);
  const [editingTicketId, setEditingTicketId] = useState(null);
  const [checkInModalTicketId, setCheckInModalTicketId] = useState(null);
  const [badgeDetailsId, setBadgeDetailsId] = useState(null);
  const [ticketTypeFilter, setTicketTypeFilter] = useState("");
  const [groupSizeFilter, setGroupSizeFilter] = useState("");
  const [groupByOrder, setGroupByOrder] = useState(false);
  const [checkInStatusFilter, setCheckInStatusFilter] = useState("");
  const [expandedHiddenOrderId, setExpandedHiddenOrderId] = useState(null);
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const [statsDrawerOpen, setStatsDrawerOpen] = useState(() => !isMobile);
  const [stickyBarStuck, setStickyBarStuck] = useState(false);
  const [stickyBarHeight, setStickyBarHeight] = useState(52);
  const stickyBarRef = useRef(null);
  const stickySentinelRef = useRef(null);
  const statsWidgetRef = useRef(null);
  const statsWidgetFixedRef = useRef(null);
  const statsWidgetSentinelRef = useRef(null);
  const [statsWidgetStuck, setStatsWidgetStuck] = useState(false);
  const [statsWidgetHeight, setStatsWidgetHeight] = useState(52);

  // Sync ticketType query param to filter when URL changes
  const ticketTypeFromUrl = query.get("ticketType") || "";
  useEffect(() => {
    setTicketTypeFilter(ticketTypeFromUrl);
  }, [ticketTypeFromUrl]);  

  useEffect(() => {
    let subscriber;
    if (eventId) {
      subscriber = dispatch(beginListeningToTickets(eventId));
    }
    return () => {
      if (subscriber) {
        subscriber();
      }
    };
  }, [eventId, dispatch]);

  useEffect(() => {
    if (eventId) {
      dispatch(fetchSessionsForEvent({ eventId }));
    }
  }, [eventId, dispatch]);

  // Exclude cancelled tickets from attendees list
  const activeTickets = (tickets || []).filter((t) => !t.cancellation);

  // Calculate filtered tickets and available group sizes for the validation effect
  // This needs to happen before early returns but we'll guard against null tickets
  const checkInSessionId = query.get("sessionId");
  let preFilteredTickets = activeTickets;
  preFilteredTickets = filterTicketsWithSearch(
    preFilteredTickets,
    searchText,
    editingTicketId,
    programBadgeTicketId,
  );
  preFilteredTickets = filterTicketsBySession(
    preFilteredTickets,
    checkInSessionId,
    sessions,
  );
  preFilteredTickets = filterByTicketType(preFilteredTickets, ticketTypeFilter);
  const preAvailableGroupSizes = calculateGroupSizes(preFilteredTickets);

  // Clear group size filter if the selected value is no longer available
  useEffect(() => {
    if (groupSizeFilter && preAvailableGroupSizes.length > 0) {
      const groupSizeValue = parseInt(groupSizeFilter, 10);
      const isStillAvailable = preAvailableGroupSizes.some(
        ({ groupSize }) => groupSize === groupSizeValue,
      );
      if (!isStillAvailable) {
        setGroupSizeFilter("");
      }
    }
  }, [preAvailableGroupSizes, groupSizeFilter]);

  let ndef;
  try {
    if (window.NDEFReader) {
      // eslint-disable-next-line no-undef
      ndef = new NDEFReader();
    }
  } catch {
    logger.warn("error creating NDEFReader");
  }

  const canProgramBadges = userIsAdmin && Boolean(ndef);

  const scanModalOpen = hash === "#scanning";
  const checkInTicketsModalOpen = hash === "#checkIn";
  const programBadgesModalOpen = hash === "#programBadges";

  let filteredTickets = activeTickets;
  filteredTickets = filterTicketsWithSearch(
    filteredTickets,
    searchText,
    editingTicketId,
    programBadgeTicketId,
  );
  filteredTickets = filterTicketsBySession(
    filteredTickets,
    checkInSessionId,
    sessions,
  );
  const ticketsBeforeTicketTypeFilter = filteredTickets;
  filteredTickets = filterByTicketType(filteredTickets, ticketTypeFilter);

  // Calculate available group sizes from tickets before group size filter
  const ticketsBeforeGroupSizeFilter = filteredTickets;
  const availableGroupSizes = calculateGroupSizes(filteredTickets);

  // Apply group size filter last
  filteredTickets = filterByGroupSize(filteredTickets, groupSizeFilter);

  // Tickets before check-in filter (for orderGroupCounts and for "show hidden" merge)
  const ticketsAfterOtherFilters = filteredTickets;
  const checkedInFilterCount = filterByCheckInStatus(
    ticketsAfterOtherFilters,
    checkInSessionId,
    "checked-in",
  ).length;
  const notCheckedInFilterCount = filterByCheckInStatus(
    ticketsAfterOtherFilters,
    checkInSessionId,
    "not-checked-in",
  ).length;
  const filteredTicketsWithCheckIn =
    checkInStatusFilter
      ? filterByCheckInStatus(
          ticketsAfterOtherFilters,
          checkInSessionId,
          checkInStatusFilter,
        )
      : ticketsAfterOtherFilters;

  // Order group counts from tickets BEFORE search and check-in filter (full group size in headers)
  const ticketsBeforeSearch = filterByGroupSize(
    filterByTicketType(
      filterTicketsBySession(activeTickets, checkInSessionId, sessions),
      ticketTypeFilter,
    ),
    groupSizeFilter,
  );
  const orderGroupCounts = calculateOrderGroupCounts(ticketsBeforeSearch);

  // When "show hidden" is expanded for one order, merge that order's hidden tickets into the list.
  // Include tickets hidden by check-in filter and/or by search (so expand works when only search is used).
  const hiddenForExpandedOrder =
    expandedHiddenOrderId && groupByOrder
      ? (() => {
          const orderKey = expandedHiddenOrderId;
          const inOrderAfterOther = ticketsAfterOtherFilters.filter(
            (t) => getOrderGroupKey(t) === orderKey,
          );
          const hiddenByCheckIn = inOrderAfterOther.filter(
            (t) => !filteredTicketsWithCheckIn.some((f) => f.id === t.id),
          );
          const hiddenBySearch = ticketsBeforeSearch.filter(
            (t) =>
              getOrderGroupKey(t) === orderKey &&
              !ticketsAfterOtherFilters.some((f) => f.id === t.id),
          );
          return [...hiddenByCheckIn, ...hiddenBySearch];
        })()
      : [];

  const displayTickets =
    groupByOrder && hiddenForExpandedOrder.length > 0
      ? sortTicketsByOrder([
          ...filteredTicketsWithCheckIn,
          ...hiddenForExpandedOrder,
        ])
      : groupByOrder
        ? sortTicketsByOrder(filteredTicketsWithCheckIn)
        : filteredTicketsWithCheckIn;

  // When in check-in mode with a session, baseline is session-scoped tickets (selective sessions don't "hide" other ticket types)
  const ticketsForBaselineCount =
    checkInTicketsModalOpen && checkInSessionId
      ? filterTicketsBySession(activeTickets, checkInSessionId, sessions)
      : activeTickets;

  const {
    checkedInCount,
    ticketCount,
    notCheckedInCount,
    checkInPercent,
    notCheckInPercent,
  } = ticketStats(ticketsForBaselineCount);

  const {
    checkedInCount: checkedInCountFiltered,
    ticketCount: ticketCountFiltered,
    notCheckedInCount: notCheckedInCountFiltered,
    checkInPercent: checkInPercentFiltered,
    notCheckInPercent: notCheckInPercentFiltered,
  } = ticketStats(filteredTicketsWithCheckIn);

  const handleResetFilters = () => {
    setSearchText("");
    setTicketTypeFilter("");
    setGroupSizeFilter("");
    setCheckInStatusFilter("");
    setExpandedHiddenOrderId(null);
    const searchParams = new URLSearchParams(search);
    searchParams.delete("ticketType");
    const newSearch = searchParams.toString() ? `?${searchParams.toString()}` : "";
    navigate({ pathname, search: newSearch, hash }, { replace: true });
  };

  const handleCheckInTicketsClick = () => {
    setSearchText("");
    setTicketTypeFilter("");
    setGroupSizeFilter("");
    setCheckInStatusFilter("");
    setExpandedHiddenOrderId(null);
    if (sessions?.length > 0) {
      navigate({ pathname, search: "", hash: "" }, { replace: true });
      setSelectSessionToCheckInModalOpen(true);
    } else {
      navigate({ pathname, search: "", hash: "checkIn" }, { replace: true });
    }
  };

  const filteredVisibility =
    ticketCount === ticketCountFiltered ? "hidden" : "visible";

  const checkInSession = (sessions || []).find(({ id }) => id === checkInSessionId);
  const checkInSessionName = checkInSession?.name;

  // When not in check-in mode, native sticky can fail (only sticks when scrolling down). Use Intersection Observer + fixed bar so it sticks in both directions.
  const useFakeSticky =
    !checkInTicketsModalOpen && ticketCount > ticketCountFiltered;
  useLayoutEffect(() => {
    if (!useFakeSticky) {
      setStickyBarStuck(false);
      return;
    }
    const sentinel = stickySentinelRef.current;
    if (!sentinel) return;
    const obs = new IntersectionObserver(
      ([entry]) => setStickyBarStuck(!entry.isIntersecting),
      { threshold: 0, root: null },
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [useFakeSticky]);
  useLayoutEffect(() => {
    if (!useFakeSticky || stickyBarStuck || !stickyBarRef.current) return;
    const el = stickyBarRef.current;
    const height = el.getBoundingClientRect().height;
    if (height > 0) setStickyBarHeight(height);
  }, [useFakeSticky, stickyBarStuck]);

  // In check-in mode the hidden bar is sticky (no fake sticky); measure it so the stats panel can sit below it when fixed
  useLayoutEffect(() => {
    if (!checkInTicketsModalOpen || ticketCount <= ticketCountFiltered || !stickyBarRef.current) return;
    const el = stickyBarRef.current;
    const height = el.getBoundingClientRect().height;
    if (height > 0) setStickyBarHeight(height);
  }, [checkInTicketsModalOpen, ticketCount, ticketCountFiltered]);

  // Stats widget: fake sticky (Intersection Observer) so it sticks when scrolling like the hidden-tickets bar. Re-run when mode changes so observer is attached for both check-in and non-check-in.
  useLayoutEffect(() => {
    setStatsWidgetStuck(false);
    const sentinel = statsWidgetSentinelRef.current;
    if (!sentinel) return;
    const obs = new IntersectionObserver(
      ([entry]) => setStatsWidgetStuck(!entry.isIntersecting),
      { threshold: 0, root: null },
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [checkInTicketsModalOpen]);
  useLayoutEffect(() => {
    if (statsWidgetStuck || !statsWidgetRef.current) return;
    const el = statsWidgetRef.current;
    const height = el.getBoundingClientRect().height;
    if (height > 0) setStatsWidgetHeight(height);
  }, [statsWidgetStuck]);

  // When stats widget is stuck (fixed), keep spacer height in sync so expand/collapse doesn't overlap the list
  useLayoutEffect(() => {
    if (!statsWidgetStuck || !statsWidgetFixedRef.current) return;
    const el = statsWidgetFixedRef.current;
    const ro = new ResizeObserver(() => {
      const height = el.getBoundingClientRect().height;
      if (height > 0) setStatsWidgetHeight(height);
    });
    ro.observe(el);
    const height = el.getBoundingClientRect().height;
    if (height > 0) setStatsWidgetHeight(height);
    return () => ro.disconnect();
  }, [statsWidgetStuck]);

  if (!event || !tickets) return null;

  return (
    <Box
      marginTop={0}
      marginBottom={{ xs: "90px", md: "0px" }}
      position={"relative"}
      data-testid={checkInTicketsModalOpen ? "check-in-tickets-view" : undefined}
    >
      {checkInTicketsModalOpen && (
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => {
                const searchParams = new URLSearchParams(search);
                searchParams.delete("sessionId");
                const newSearch = searchParams.toString()
                  ? `?${searchParams.toString()}`
                  : "";
                navigate({ pathname, search: newSearch, hash: "" }, {
                  replace: true,
                });
              }}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography
              sx={{
                ml: 2,
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: "3",
                WebkitBoxOrient: "vertical",
              }}
              variant="h6"
              component="div"
            >
              Check In Tickets
            </Typography>
            <IconButton
              color="inherit"
              onClick={() => handleShowQrCode(publicUrl)}
              aria-label="Show QR code"
            >
              <QrCodeIcon color="inherit" />
            </IconButton>
            <IconButton
              color="inherit"
              onClick={() => {
                navigate({
                  pathname,
                  hash: "scanning",
                  search: checkInSessionId ? `?sessionId=${checkInSessionId}` : search,
                });
              }}
              aria-label="Scan"
            >
              <CameraAltIcon color="inherit" />
            </IconButton>
          </Toolbar>
        </AppBar>
      )}
      <Box sx={checkInTicketsModalOpen ? { px: 2, py: 2 } : {}}>
      {checkInSessionName && checkInTicketsModalOpen && (
        <Box sx={{ pt: 2, pb: 2 }}>
          <Typography variant="h6" component="div">
            {checkInSessionName}
          </Typography>
        </Box>
      )}
      <ProgramBadgeModel
        open={Boolean(programBadgeTicketId)}
        ticketId={programBadgeTicketId}
        event={event}
        handleClose={() => {
          setProgramBadgeTicketId(null);
          ndef.onreadingerror = null;
          ndef.onreading = null;
        }}
        ndef={ndef}
      />
      <BadgeDetailsModal
        open={Boolean(badgeDetailsId)}
        ticketId={badgeDetailsId}
        event={event}
        handleClose={() => {
          setBadgeDetailsId(null);
        }}
        handleClear={async (ticketId) => {
          await dispatch(
            updateBadgeForTicket({
              eventId,
              ticketId,
              serialNumber: null,
            }),
          );
          setBadgeDetailsId(null);
        }}
      />
      <CheckInsInfoModal
        open={Boolean(checkInModalTicketId)}
        ticketId={checkInModalTicketId}
        event={event}
        handleClose={() => {
          setCheckInModalTicketId(null);
        }}
        handleCheckIn={(checkedIn, sessionId) => {
          dispatch(
            checkInTicket({
              eventId,
              sessionId,
              ticketId: checkInModalTicketId,
              checkedIn,
            }),
          );
        }}
      />
      <SelectSessionToCheckInModal
        {...{
          open: selectSessionToCheckInModalOpen,
          event,
          handleClose: () => {
            setSelectSessionToCheckInModalOpen(false);
          },
        }}
      />
      <ScanTicketsModal
        {...{
          open: scanModalOpen,
          event,
          tickets,
          sessionId: checkInSessionId,
          handleClose: async () => {
            window.history.back();
          },
          handleEditChange: (ticketId) => {
            setEditingTicketId(ticketId);
          },
        }}
      />
      <FullScreenDialog open={false} />
      <ProgramBadgesModal
        {...{
          open: programBadgesModalOpen,
          event: event,
          tickets: filteredTickets,
          handleClose: () => {
            window.history.back();
          },
          searchText: searchText,
          setEditingTicketId: setEditingTicketId,
          handleSearchTextChange: setSearchText,
          onNavigateToOrderDetails: userIsAdmin
            ? (orderId) => navigate(`../orders/${orderId}`)
            : undefined,
          handleCheckInClick: async (ticket, programmed) => {
            const { id: ticketId } = ticket;
            if (programmed) {
              setProgramBadgeTicketId(ticketId);
            } else {
              setBadgeDetailsId(ticketId);
            }
          },
        }}
      />
      <NewTicketModal
        {...{
          open: newTicketModalOpen,
          event,
          handleSave: (orderData) => {
            dispatch(createOrder(orderData));
          },
          handleClose: async () => {
            await setNewTicketModalOpen(false);
          },
        }}
      />
      {!checkInTicketsModalOpen && (
        <Box
          display="flex"
          flexWrap="wrap"
          gap={2}
          alignItems="stretch"
          marginBottom={"8px"}
          sx={{
            width: "100%",
            justifyContent: { xs: "stretch", md: "flex-end" },
          }}
        >
          {canProgramBadges && (
            <Button
              data-testid="program-badges-button"
              startIcon={<BadgeIcon />}
              onClick={() => {
                navigate({ hash: "programBadges" });
              }}
              sx={{ flex: { xs: 1, md: "none" }, minWidth: 0 }}
            >
              Program Badges
            </Button>
          )}
          <Button
            data-testid="add-ticket-button"
            startIcon={<AddIcon />}
            onClick={() => {
              setNewTicketModalOpen(true);
            }}
            sx={{ flex: { xs: 1, md: "none" }, minWidth: 0 }}
          >
            Add Ticket
          </Button>
          <Button
            data-testid="check-in-tickets-button"
            startIcon={<CheckIcon />}
            onClick={handleCheckInTicketsClick}
            sx={{ flex: { xs: 1, md: "none" }, minWidth: 0 }}
          >
            Check In Tickets
          </Button>
        </Box>
      )}
      {ticketCount > ticketCountFiltered && (() => {
        const barContent = (
          <>
            <Typography variant="body2" color="text.secondary">
              {ticketCount - ticketCountFiltered === 1
                ? "1 ticket hidden by filters"
                : `${ticketCount - ticketCountFiltered} tickets hidden by filters`}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={handleResetFilters}
              data-testid="reset-filters-button"
            >
              Reset
            </Button>
          </>
        );
        const barSx = {
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          py: 1.5,
          px: 2,
          mb: 1,
          width: "100%",
          boxSizing: "border-box",
          bgcolor: "background.paper",
          border: "2px solid",
          borderColor: "warning.main",
          zIndex: 10,
        };
        if (useFakeSticky) {
          return (
            <>
              <Box ref={stickySentinelRef} sx={{ height: 1, minHeight: 1, overflow: "hidden", pointerEvents: "none" }} aria-hidden="true" />
              {stickyBarStuck ? (
                <>
                  <Box sx={{ height: stickyBarHeight, mb: 1, flexShrink: 0 }} aria-hidden="true" />
                  <Box
                    sx={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      right: 0,
                      zIndex: 11,
                    }}
                  >
                    <Container sx={{ px: { xs: 0, md: 3 } }}>
                      <Box sx={{ px: 2 }}>
                        <Box
                          ref={stickyBarRef}
                          sx={barSx}
                          data-testid="tickets-hidden-by-filters"
                        >
                          {barContent}
                        </Box>
                      </Box>
                    </Container>
                  </Box>
                </>
              ) : (
                <Box
                  ref={stickyBarRef}
                  sx={barSx}
                  data-testid="tickets-hidden-by-filters"
                >
                  {barContent}
                </Box>
              )}
            </>
          );
        }
        return (
          <Box
            ref={stickyBarRef}
            sx={{
              ...barSx,
              position: "sticky",
              top: 0,
              zIndex: 11,
            }}
            data-testid="tickets-hidden-by-filters"
          >
            {barContent}
          </Box>
        );
      })()}
      {(
        <>
          <Box ref={statsWidgetSentinelRef} sx={{ height: 1, minHeight: 1, overflow: "hidden", pointerEvents: "none" }} aria-hidden="true" />
          {statsWidgetStuck ? (
            <>
              <Box sx={{ height: ticketCount > ticketCountFiltered ? stickyBarHeight + statsWidgetHeight : statsWidgetHeight, mb: 2, flexShrink: 0 }} aria-hidden="true" />
              <Box
                sx={{
                  position: "fixed",
                  top: ticketCount > ticketCountFiltered ? stickyBarHeight : 0,
                  left: 0,
                  right: 0,
                  zIndex: 10,
                }}
              >
                <Container sx={{ px: { xs: 0, md: 3 } }}>
                  <Box sx={{ px: 2 }}>
                    <Box
                      ref={statsWidgetFixedRef}
                      sx={{
                        backgroundColor: "background.paper",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        boxShadow: 1,
                        overflow: "hidden",
                      }}
                      data-testid="stats-widget"
                    >
                      <Button
                        fullWidth
                        data-testid="stats-widget-toggle"
                        onClick={() => setStatsDrawerOpen((open) => !open)}
                        startIcon={<BarChartIcon />}
                        endIcon={statsDrawerOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        sx={{ justifyContent: "flex-start", textTransform: "none", py: 1.5 }}
                      >
                        {ticketCount} ticket{ticketCount !== 1 ? "s" : ""}
                        {ticketCount !== ticketCountFiltered && ` (${ticketCountFiltered} shown)`}
                      </Button>
                      <Collapse in={statsDrawerOpen}>
                        <Box sx={{ px: 2, pb: 2, pt: 0 }}>
                          <Grid container spacing={1} sx={{ width: "100%" }}>
                            <Grid item xs={6}>
                              <Typography color={"primary.lightText"}>Total Tickets</Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography>{ticketCount}</Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography visibility={filteredVisibility} color={"green"}>
                                {ticketCountFiltered}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography color={"primary.lightText"}>Checked In:</Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography>{`${checkedInCount} (${checkInPercent}%)`}</Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography visibility={filteredVisibility} color={"green"}>
                                {`${checkedInCountFiltered} (${checkInPercentFiltered}%)`}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography color={"primary.lightText"}>Not Checked In:</Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography>{`${notCheckedInCount} (${notCheckInPercent}%)`}</Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography visibility={filteredVisibility} color={"green"}>
                                {`${notCheckedInCountFiltered} (${notCheckInPercentFiltered}%)`}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </Box>
                  </Box>
                </Container>
              </Box>
            </>
          ) : (
            <Box
              ref={statsWidgetRef}
              sx={{
                backgroundColor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                boxShadow: 1,
                mb: 2,
                overflow: "hidden",
              }}
              data-testid="stats-widget"
            >
              <Button
                fullWidth
                data-testid="stats-widget-toggle"
                onClick={() => setStatsDrawerOpen((open) => !open)}
                startIcon={<BarChartIcon />}
                endIcon={statsDrawerOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ justifyContent: "flex-start", textTransform: "none", py: 1.5 }}
              >
                {ticketCount} ticket{ticketCount !== 1 ? "s" : ""}
                {ticketCount !== ticketCountFiltered && ` (${ticketCountFiltered} shown)`}
              </Button>
              <Collapse in={statsDrawerOpen}>
                <Box sx={{ px: 2, pb: 2, pt: 0 }}>
                  <Grid container spacing={1} sx={{ width: "100%" }}>
                    <Grid item xs={6}>
                      <Typography color={"primary.lightText"}>Total Tickets</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography>{ticketCount}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography visibility={filteredVisibility} color={"green"}>
                        {ticketCountFiltered}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography color={"primary.lightText"}>Checked In:</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography>{`${checkedInCount} (${checkInPercent}%)`}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography
                        visibility={filteredVisibility}
                        color={"green"}
                      >{`${checkedInCountFiltered} (${checkInPercentFiltered}%)`}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography color={"primary.lightText"}>Not Checked In:</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography>{`${notCheckedInCount} (${notCheckInPercent}%)`}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography
                        visibility={filteredVisibility}
                        color={"green"}
                      >{`${notCheckedInCountFiltered} (${notCheckInPercentFiltered}%)`}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Collapse>
            </Box>
          )}
        </>
      )}
      {/* Desktop: inline filters and group toggle. Mobile: filters in drawer, toggle in drawer. */}
      {!isMobile && (
        <>
          <Box display={"flex"} marginBottom={"40px"} sx={{ width: "100%" }}>
            <Box
              flexGrow={"1"}
              display={"flex"}
              sx={{
                flexDirection: { xs: "column", sm: "column", md: "row" },
                alignItems: { xs: "stretch", sm: "stretch", md: "center" },
                gap: { xs: "20px", sm: "20px", md: 2 },
                width: "100%",
                flexWrap: { xs: "wrap", md: "nowrap" },
              }}
            >
              <TextField
                id={"ticketSearch"}
                name={"ticketSearch"}
                data-testid="ticket-search-input"
                label="Search"
                value={searchText}
                sx={{ width: { xs: "100%" }, flex: { md: "1 1 0" }, minWidth: { md: 140 } }}
                variant="outlined"
                onChange={(e) => {
                  setEditingTicketId(null);
                  setSearchText(e.target.value);
                }}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      data-testid="clear-search-button"
                      onClick={() => {
                        setEditingTicketId(null);
                        setSearchText("");
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  ),
                }}
              />

              <FormControl
                sx={{
                  width: { xs: "100%" },
                  flex: { md: "1 1 0" },
                  minWidth: { md: 140 },
                  marginTop: { xs: 0, sm: 0, md: 0 },
                  marginLeft: { sm: "0px", xs: "0px", md: 0 },
                }}
              >
                <InputLabel shrink id="ticketType-label" sx={{ backgroundColor: "background.paper", px: 0.5 }}>
                  Ticket Type
                </InputLabel>
                <Select
                  id="ticketType-selector"
                  name="ticketType-selector"
                  data-testid="ticket-type-selector"
                  labelId="ticketType-label"
                  label="Ticket Type"
                  displayEmpty
                  value={ticketTypeFilter}
                  renderValue={(value) =>
                    value === ""
                      ? "All Ticket Types"
                      : (ticketTypes || []).find((t) => t.key === value)?.name ?? value
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    setTicketTypeFilter(value);
                    const searchParams = new URLSearchParams(search);
                    if (value) {
                      searchParams.set("ticketType", value);
                    } else {
                      searchParams.delete("ticketType");
                    }
                    const queryString = searchParams.toString();
                    navigate(
                      `${pathname}${queryString ? `?${queryString}` : ""}${hash || ""}`,
                      { replace: true },
                    );
                  }}
                >
                  {[
                    <MenuItem key={`show-any`} value="">
                      <Typography color={"primary.lightText"}>
                        All Ticket Types
                      </Typography>
                    </MenuItem>,
                    ...(ticketTypes || []).map(({ key, name }) => {
                      const count = filterByTicketType(
                        ticketsBeforeTicketTypeFilter,
                        key,
                      ).length;
                      return (
                        <MenuItem key={`ticketTypeFilter-${key}`} value={key}>
                          <Tooltip title={name} enterDelay={200}>
                            <Box>
                              <Typography
                                sx={{
                                  overflow: "hidden",
                                  maxWidth: "300px",
                                  textOverflow: "ellipsis",
                                  display: "-webkit-box",
                                  WebkitLineClamp: "1",
                                  WebkitBoxOrient: "vertical",
                                  marginTop: "0px",
                                  whiteSpace: "normal",
                                }}
                                component="div"
                              >
                                {name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {count} ticket{count !== 1 ? "s" : ""}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </MenuItem>
                      );
                    }),
                  ]}
                </Select>
              </FormControl>

              <FormControl
                sx={{
                  width: { xs: "100%" },
                  flex: { md: "1 1 0" },
                  minWidth: { md: 140 },
                  marginTop: { sm: "20px", xs: "20px", md: 0 },
                  marginLeft: { sm: "0px", xs: "0px", md: 0 },
                }}
              >
                <InputLabel shrink id="groupSize-label" sx={{ backgroundColor: "background.paper", px: 0.5 }}>
                  Group Size
                </InputLabel>
                <Select
                  id="groupSize-selector"
                  name="groupSize-selector"
                  data-testid="group-size-selector"
                  labelId="groupSize-label"
                  label="Group Size"
                  displayEmpty
                  value={groupSizeFilter}
                  renderValue={(value) => {
                    if (value === "" || value == null) return "All Group Sizes";
                    const g = availableGroupSizes.find(
                      (x) => String(x.groupSize) === String(value),
                    );
                    return g
                      ? `${g.groupSize} ${g.groupSize === 1 ? "ticket" : "tickets"} (${g.count} ${g.count === 1 ? "order" : "orders"})`
                      : String(value);
                  }}
                  onChange={(e) => {
                    setGroupSizeFilter(e.target.value);
                  }}
                >
                  {[
                    <MenuItem key="show-any-group-size" value="">
                      <Typography color={"primary.lightText"}>
                        All Group Sizes
                      </Typography>
                    </MenuItem>,
                    ...availableGroupSizes.map(({ groupSize, count }) => {
                      const ticketCount = filterByGroupSize(
                        ticketsBeforeGroupSizeFilter,
                        String(groupSize),
                      ).length;
                      return (
                        <MenuItem key={`groupSize-${groupSize}`} value={groupSize}>
                          <Box>
                            <Typography>
                              {groupSize} {groupSize === 1 ? "ticket" : "tickets"} ({count} {count === 1 ? "order" : "orders"})
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {ticketCount} ticket{ticketCount !== 1 ? "s" : ""}
                            </Typography>
                          </Box>
                        </MenuItem>
                      );
                    }),
                  ]}
                </Select>
              </FormControl>

              <FormControl
                sx={{
                  width: { xs: "100%" },
                  flex: { md: "1 1 0" },
                  minWidth: { md: 140 },
                  marginTop: { sm: "20px", xs: "20px", md: 0 },
                  marginLeft: { sm: "0px", xs: "0px", md: 0 },
                }}
              >
                <InputLabel shrink id="checkInStatus-label" sx={{ backgroundColor: "background.paper", px: 0.5 }}>
                  Check-in status
                </InputLabel>
                <Select
                  id="checkInStatus-selector"
                  name="checkInStatus-selector"
                  data-testid="check-in-status-selector"
                  labelId="checkInStatus-label"
                  label="Check-in status"
                  displayEmpty
                  value={checkInStatusFilter}
                  renderValue={(value) =>
                    value === ""
                      ? "Any Check-in status"
                      : value === "checked-in"
                        ? "Checked in"
                        : value === "not-checked-in"
                          ? "Not checked in"
                          : value
                  }
                  onChange={(e) => {
                    setCheckInStatusFilter(e.target.value);
                    setExpandedHiddenOrderId(null);
                  }}
                >
                  <MenuItem value="">
                    <Typography color={"primary.lightText"}>
                      Any Check-in status
                    </Typography>
                  </MenuItem>
                  <MenuItem value="checked-in">
                    <Box>
                      <Typography>Checked in</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {checkedInFilterCount} ticket{checkedInFilterCount !== 1 ? "s" : ""}
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="not-checked-in">
                    <Box>
                      <Typography>Not checked in</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {notCheckedInFilterCount} ticket{notCheckedInFilterCount !== 1 ? "s" : ""}
                      </Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
          <Box
            display="flex"
            justifyContent="flex-end"
            sx={{ marginBottom: 1 }}
          >
            <ToggleButtonGroup
              value={groupByOrder ? "group" : "list"}
              exclusive
              onChange={(_, value) => {
                if (value !== null) setGroupByOrder(value === "group");
              }}
              aria-label="List view"
              sx={{
                "& .MuiToggleButton-root": {
                  color: "primary.main",
                },
                "& .MuiToggleButton-root.Mui-selected": {
                  color: "primary.main",
                  backgroundColor: "action.selected",
                },
              }}
            >
              <ToggleButton value="list" aria-label="Flat list">
                <Tooltip title="Flat list">
                  <ViewListIcon />
                </Tooltip>
              </ToggleButton>
              <ToggleButton
                value="group"
                aria-label="Group by order"
                data-testid="group-by-order-checkbox"
              >
                <Tooltip title="Group by order">
                  <ViewAgendaIcon />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </>
      )}

      {/* Mobile: slide-out filters drawer (shared component, renders above content). Portal to body when not in check-in so FAB stays fixed despite overflow-x: hidden on parent. */}
      {isMobile && (
        <MobileFiltersPanel
          open={filtersDrawerOpen}
          onClose={() => setFiltersDrawerOpen(false)}
          onOpenClick={() => setFiltersDrawerOpen(true)}
          badgeCount={[ticketTypeFilter, groupSizeFilter, checkInStatusFilter].filter(Boolean).length}
          dataTestId="filters-drawer"
          zIndex={theme.zIndex.drawer}
          scrollRestoreDeps={[searchText]}
          portalToBody={!checkInTicketsModalOpen}
        >
          <TextField
            id="ticketSearch-drawer"
            name="ticketSearch-drawer"
            data-testid="ticket-search-input"
            label="Search"
            value={searchText}
            fullWidth
            variant="outlined"
            onChange={(e) => {
              setEditingTicketId(null);
              setSearchText(e.target.value);
            }}
            InputProps={{
              endAdornment: (
                <IconButton
                  data-testid="clear-search-button"
                  onClick={() => {
                    setEditingTicketId(null);
                    setSearchText("");
                  }}
                >
                  <ClearIcon />
                </IconButton>
              ),
            }}
          />
          <FormControl fullWidth>
            <InputLabel shrink id="ticketType-label-drawer" sx={{ backgroundColor: "background.paper", px: 0.5 }}>
              Ticket Type
            </InputLabel>
            <Select
              id="ticketType-selector-drawer"
              name="ticketType-selector-drawer"
              data-testid="ticket-type-selector"
              labelId="ticketType-label-drawer"
              label="Ticket Type"
              displayEmpty
              value={ticketTypeFilter}
              renderValue={(value) =>
                value === ""
                  ? "All Ticket Types"
                  : (ticketTypes || []).find((t) => t.key === value)?.name ?? value
              }
              onChange={(e) => {
                const value = e.target.value;
                setTicketTypeFilter(value);
                const searchParams = new URLSearchParams(search);
                if (value) {
                  searchParams.set("ticketType", value);
                } else {
                  searchParams.delete("ticketType");
                }
                const queryString = searchParams.toString();
                navigate(
                  `${pathname}${queryString ? `?${queryString}` : ""}${hash || ""}`,
                  { replace: true },
                );
              }}
            >
              <MenuItem value="">
                <Typography color={"primary.lightText"}>
                  All Ticket Types
                </Typography>
              </MenuItem>
              {(ticketTypes || []).map(({ key, name }) => {
                const count = filterByTicketType(
                  ticketsBeforeTicketTypeFilter,
                  key,
                ).length;
                return (
                  <MenuItem key={`ticketTypeFilter-drawer-${key}`} value={key}>
                    <Box>
                      <Typography
                        sx={{
                          overflow: "hidden",
                          maxWidth: "260px",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: "vertical",
                        }}
                        component="div"
                      >
                        {name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {count} ticket{count !== 1 ? "s" : ""}
                      </Typography>
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel shrink id="groupSize-label-drawer" sx={{ backgroundColor: "background.paper", px: 0.5 }}>
              Group Size
            </InputLabel>
            <Select
              id="groupSize-selector-drawer"
              name="groupSize-selector-drawer"
              data-testid="group-size-selector"
              labelId="groupSize-label-drawer"
              label="Group Size"
              displayEmpty
              value={groupSizeFilter}
              renderValue={(value) => {
                if (value === "" || value == null) return "All Group Sizes";
                const g = availableGroupSizes.find(
                  (x) => String(x.groupSize) === String(value),
                );
                return g
                  ? `${g.groupSize} ${g.groupSize === 1 ? "ticket" : "tickets"} (${g.count} ${g.count === 1 ? "order" : "orders"})`
                  : String(value);
              }}
              onChange={(e) => setGroupSizeFilter(e.target.value)}
            >
              <MenuItem value="">
                <Typography color={"primary.lightText"}>
                  All Group Sizes
                </Typography>
              </MenuItem>
              {availableGroupSizes.map(({ groupSize, count }) => {
                const ticketCount = filterByGroupSize(
                  ticketsBeforeGroupSizeFilter,
                  String(groupSize),
                ).length;
                return (
                  <MenuItem key={`groupSize-drawer-${groupSize}`} value={groupSize}>
                    <Box>
                      <Typography>
                        {groupSize} {groupSize === 1 ? "ticket" : "tickets"} ({count} {count === 1 ? "order" : "orders"})
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {ticketCount} ticket{ticketCount !== 1 ? "s" : ""}
                      </Typography>
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel shrink id="checkInStatus-label-drawer" sx={{ backgroundColor: "background.paper", px: 0.5 }}>
              Check-in status
            </InputLabel>
            <Select
              id="checkInStatus-selector-drawer"
              name="checkInStatus-selector-drawer"
              data-testid="check-in-status-selector"
              labelId="checkInStatus-label-drawer"
              label="Check-in status"
              displayEmpty
              value={checkInStatusFilter}
              renderValue={(value) =>
                value === ""
                  ? "Any Check-in status"
                  : value === "checked-in"
                    ? "Checked in"
                    : value === "not-checked-in"
                      ? "Not checked in"
                      : value
              }
              onChange={(e) => {
                setCheckInStatusFilter(e.target.value);
                setExpandedHiddenOrderId(null);
              }}
            >
              <MenuItem value="">
                <Typography color={"primary.lightText"}>
                  Any Check-in status
                </Typography>
              </MenuItem>
              <MenuItem value="checked-in">
                <Box>
                  <Typography>Checked in</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {checkedInFilterCount} ticket{checkedInFilterCount !== 1 ? "s" : ""}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="not-checked-in">
                <Box>
                  <Typography>Not checked in</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {notCheckedInFilterCount} ticket{notCheckedInFilterCount !== 1 ? "s" : ""}
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
            View
          </Typography>
          <ToggleButtonGroup
            value={groupByOrder ? "group" : "list"}
            exclusive
            fullWidth
            onChange={(_, value) => {
              if (value !== null) setGroupByOrder(value === "group");
            }}
            aria-label="List view"
            sx={{
              "& .MuiToggleButton-root": {
                color: "primary.main",
              },
              "& .MuiToggleButton-root.Mui-selected": {
                color: "primary.main",
                backgroundColor: "action.selected",
              },
            }}
          >
            <ToggleButton value="list" aria-label="Flat list">
              <ViewListIcon sx={{ mr: 0.5 }} />
              <Typography variant="body2">List</Typography>
            </ToggleButton>
            <ToggleButton
              value="group"
              aria-label="Group by order"
              data-testid="group-by-order-checkbox"
            >
              <ViewAgendaIcon sx={{ mr: 0.5 }} />
              <Typography variant="body2">By order</Typography>
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="contained"
            onClick={() => setFiltersDrawerOpen(false)}
            sx={{ mt: 2 }}
            data-testid="apply-filters-drawer"
          >
            Done
          </Button>
        </MobileFiltersPanel>
      )}
      <TicketList
        tickets={displayTickets}
        eventCountByAttendeeId={eventCountByAttendeeId}
        groupByOrder={groupByOrder}
        orderGroupCounts={orderGroupCounts}
        expandedHiddenOrderId={expandedHiddenOrderId}
        onToggleHiddenOrder={setExpandedHiddenOrderId}
        checkInSessionId={checkInSessionId}
        ticketsBeforeCheckInFilter={
          checkInTicketsModalOpen
            ? ticketsAfterOtherFilters
            : checkInStatusFilter
              ? ticketsAfterOtherFilters
              : null
        }
        handleCheckInClick={
          checkInTicketsModalOpen
            ? async (ticket, checkedIn) => {
                await dispatch(
                  checkInTicket({
                    eventId,
                    sessionId: checkInSessionId,
                    ticketId: ticket.id,
                    checkedIn,
                  }),
                );
              }
            : (ticket) => {
                setCheckInModalTicketId(ticket.id);
              }
        }
        event={event}
        handleEditChange={(ticketId) => {
          setEditingTicketId(ticketId);
        }}
        onOrderIdClicked={
          userIsAdmin ? (orderId) => navigate(`../orders/${orderId}`) : undefined
        }
        onOrderIdFilterClick={(orderId) => {
          setSearchText(orderId);
        }}
        handleTicketTypeNameClicked={(ticketTypeKey) => {
          setTicketTypeFilter(ticketTypeKey);
          const searchParams = new URLSearchParams(search);
          if (ticketTypeKey) {
            searchParams.set("ticketType", ticketTypeKey);
          } else {
            searchParams.delete("ticketType");
          }
          const queryString = searchParams.toString();
          navigate(
            `${pathname}${queryString ? `?${queryString}` : ""}${hash || ""}`,
            { replace: true },
          );
        }}
      />
      </Box>
    </Box>
  );
};

export default EventTickets;
